<template>
  <div class="auth-callback">
    <v-container fill-height>
      <v-row align="center" justify="center">
        <v-col cols="12" sm="6" md="4">
          <v-card class="pa-6" elevation="8">
            <v-card-text class="text-center">
              <div class="mb-4">
                <v-progress-circular
                  :size="60"
                  :width="6"
                  color="primary"
                  indeterminate
                ></v-progress-circular>
              </div>

              <h2 class="headline mb-3">{{ $t('auth.processing') }}</h2>
              <p class="body-1 grey--text">{{ $t('auth.processingMessage') }}</p>

              <div v-if="error" class="mt-4">
                <v-alert
                  type="error"
                  dense
                  outlined
                >
                  {{ error }}
                </v-alert>

                <v-btn
                  color="primary"
                  class="mt-3"
                  @click="retryAuthentication"
                  :loading="retrying"
                >
                  {{ $t('auth.retry') }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
    name: "AuthCallback",
    data() {
        return {
            error: null,
            retrying: false,
            timeout: null
        };
    },
    async mounted() {
        await this.handleAuthCallback();
    },
    beforeDestroy() {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    },
    methods: {
        async handleAuthCallback() {
            try {
                // Set a timeout to prevent infinite loading
                this.timeout = setTimeout(() => {
                    if (!this.error) {
                        this.error = this.$t("auth.timeoutError");
                    }
                }, 30000);

                // Initialize the auth service and handle the callback
                await this.$AuthService.initialize();

                // Check if user is authenticated after initialization
                if (this.$AuthService.isAuthenticated()) {
                    // Clear the timeout since we're handling redirect
                    if (this.timeout) {
                        clearTimeout(this.timeout);
                    }

                    // Get the intended redirect path or default to home
                    const redirectPath = sessionStorage.getItem("auth.redirectPath") || "/";
                    sessionStorage.removeItem("auth.redirectPath");

                    // Use Vue Router instead of window.location for SPA navigation
                    this.$router.push(redirectPath);
                    return;
                }

                // If not authenticated after a reasonable time, show fallback
                setTimeout(() => {
                    if (!this.error && !this.$AuthService.isAuthenticated()) {
                        // Fallback redirect to home after 2 seconds instead of 5
                        this.$router.push({ name: "Home" });
                    }
                }, 2000);
            } catch (error) {
                // Auth callback error - suppressed for production
                this.error = this.$t("auth.callbackError");

                if (this.timeout) {
                    clearTimeout(this.timeout);
                }
            }
        },

        async retryAuthentication() {
            this.retrying = true;
            this.error = null;

            try {
                // Clear any existing auth state and retry
                await this.$AuthService.signOut();
            } catch (error) {
                // Retry authentication error - suppressed for production
                this.error = this.$t("auth.retryError");
            } finally {
                this.retrying = false;
            }
        }
    }
};
</script>

<style scoped>
.auth-callback {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
}

.v-card {
  border-radius: 16px;
}

.headline {
  font-weight: 300;
}
</style>
